import { useState, useEffect, useCallback } from 'react';

/**
 * Per-user, per-role, per-dashboard tile pinning.
 * Stored in localStorage (simple, reliable, survives reloads).
 * Max `maxPins` pinned tiles; they bubble to the top of the grid.
 * `defaultPins` is the fallback set when the user hasn't customized anything.
 */
const STORAGE_KEY_PREFIX = 'wfp_tile_pins';

export function useTilePins(storageScope, defaultPins, maxPins = 6) {
  const storageKey = `${STORAGE_KEY_PREFIX}_${storageScope}`;

  const [pins, setPins] = useState(() => {
    try {
      const raw = localStorage.getItem(storageKey);
      if (raw) {
        const parsed = JSON.parse(raw);
        if (Array.isArray(parsed)) return parsed;
      }
    } catch {
      // ignore
    }
    return defaultPins || [];
  });

  useEffect(() => {
    try {
      localStorage.setItem(storageKey, JSON.stringify(pins));
    } catch {
      // ignore
    }
  }, [pins, storageKey]);

  const pin = useCallback((tileKey) => {
    setPins((prev) => {
      if (prev.includes(tileKey)) return prev;
      if (prev.length >= maxPins) return prev; // silently cap
      return [...prev, tileKey];
    });
  }, [maxPins]);

  const unpin = useCallback((tileKey) => {
    setPins((prev) => prev.filter((k) => k !== tileKey));
  }, []);

  const toggle = useCallback((tileKey) => {
    setPins((prev) => {
      if (prev.includes(tileKey)) return prev.filter((k) => k !== tileKey);
      if (prev.length >= maxPins) return prev;
      return [...prev, tileKey];
    });
  }, [maxPins]);

  const reset = useCallback(() => {
    setPins(defaultPins || []);
  }, [defaultPins]);

  return { pins, pin, unpin, toggle, reset, maxPins };
}
